'use client'

import useIdleLoading from '@/app/hooks/useIdleLoading'
import { useUserData } from '@/app/hooks/useUserData'
import { GoogleAnalytics } from '@next/third-parties/google'
import dynamic from 'next/dynamic'
import { FC, memo, useEffect, useRef, useState } from 'react'

interface Props {
	pageName: string
	siteLanguage?: string
}

const RenderGA: FC<Props> = ({ pageName, siteLanguage }) => {
	const isIdle = useIdleLoading()
	const [isGALoaded, setIsGALoaded] = useState(false)
	const { userData } = useUserData()
	const hasLoadedGA = useRef(false)

	useEffect(() => {
		if (typeof window !== 'undefined') {
			window.dataLayer = window.dataLayer || []
			window.gtag =
				window.gtag ||
				function () {
					;(window.dataLayer = window.dataLayer || []).push(arguments)
				}

			window.gtag('consent', 'default', {
				functionality_storage: 'denied',
				security_storage: 'granted',
				ad_personalization: 'denied',
				ad_storage: 'denied',
				ad_user_data: 'denied',
				analytics_storage: 'denied',
				personalization_storage: 'denied',
				region: ['MD', 'FR', 'IL'],
			})

			window.gtag('set', 'ads_data_redaction', true)
			window.gtag('set', 'url_passthrough', false)
		}
	}, [])

	useEffect(() => {
		if (isIdle && !hasLoadedGA.current) {
			const checkGtagAndFireEvent = () => {
				if (
					typeof window !== 'undefined' &&
					typeof window.gtag !== 'undefined'
				) {
					setIsGALoaded(true)
				} else {
					setTimeout(checkGtagAndFireEvent, 100)
				}
			}

			checkGtagAndFireEvent()
		}
	}, [isIdle])

	useEffect(() => {
		if (isGALoaded && userData && !hasLoadedGA.current) {
			// Flip the guard SYNCHRONOUSLY before scheduling the deferred emit.
			// `userData` (useLocalStorage) is a new object reference on every render
			// and re-subscribes to same-tab StorageEvents, so InitCaller's init-data
			// write landing inside the schedule->fire window would otherwise re-run
			// this effect with the guard still false and emit a second
			// `${pageName}_view` (THP-101 A.1).
			hasLoadedGA.current = true

			const { sessionID } = userData

			window.gtag('set', {
				user_id: sessionID,
				user_properties: {
					page_name: pageName,
					...(siteLanguage && { site_language: siteLanguage })
				},
			})

			// TODO: refactor in a way we generally emit GA events
			const fireEvent = () => {
				let eventName = `${pageName}_view`
				window.gtag('event', eventName, {
					app_name: 'Top Hotel Prices',
					screen_name: pageName,
				})
			}

			if (window.requestIdleCallback) {
				window.requestIdleCallback(fireEvent)
			} else {
				setTimeout(fireEvent, 0)
			}
		}
	}, [isGALoaded, userData, pageName, siteLanguage])

	return (
		<>
			{isIdle && (
				<GoogleAnalytics
					dataLayerName="dataLayer"
					gaId={process.env.NEXT_PUBLIC_GA_ID as string}
				/>
			)}
		</>
	)
}

export default dynamic(() => Promise.resolve(memo(RenderGA)), {
	ssr: false,
})
